/**
 * TypeScript / JavaScript language-service configuration for Monaco.
 *
 * Monaco ships with a full TS language service (the same one VS Code uses
 * in-browser), which means rich IntelliSense comes nearly for free — we
 * only need to tune a few options:
 *
 *   - Relax strictness to match what esbuild tolerates on the judge side.
 *     Starter code often lacks explicit types; strict checking would
 *     produce noisy red squiggles on code the judge would happily run.
 *   - Target modern ES so completion for newer methods (Array.prototype.at,
 *     Object.hasOwn, structuredClone, etc.) shows up.
 *   - Opt out of the implicit `react` / `react/jsx-runtime` types that get
 *     pulled in by default.
 *
 * The returned object contains `dispose`-able entries for symmetry with
 * other configurators, though the TS service is process-wide and can't be
 * "unregistered" per se — language switches don't need to undo this config.
 */
import type { Monaco } from '@monaco-editor/react';

export function configureTypeScript(monaco: Monaco): { dispose: () => void } {
  const ts = monaco.languages.typescript;

  // Compiler options for both .ts and .js virtual files.
  ts.typescriptDefaults.setCompilerOptions({
    target: ts.ScriptTarget.ES2022,
    lib: ['es2022', 'dom'],
    allowNonTsExtensions: true,
    moduleResolution: ts.ModuleResolutionKind.NodeJs,
    module: ts.ModuleKind.ESNext,
    // Lenient to match the judge's esbuild configuration: starter code
    // deliberately omits types in places.
    strict: false,
    noImplicitAny: false,
    noImplicitThis: false,
    strictNullChecks: false,
    // Don't require `import X from 'x'` when we never actually import anything.
    isolatedModules: false,
    allowSyntheticDefaultImports: true,
    esModuleInterop: true,
  });
  // Same options for the JS variant.
  ts.javascriptDefaults.setCompilerOptions({
    target: ts.ScriptTarget.ES2022,
    lib: ['es2022', 'dom'],
    allowNonTsExtensions: true,
    moduleResolution: ts.ModuleResolutionKind.NodeJs,
    module: ts.ModuleKind.ESNext,
    checkJs: false,
    allowJs: true,
  });

  // We still want squiggles for real syntax errors, but not for
  // semantic issues that result from the leniency above.
  ts.typescriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: false,
    noSyntaxValidation: false,
  });
  ts.javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: true, // too noisy on starter-code JS
    noSyntaxValidation: false,
  });

  // `dispose` is a no-op because Monaco's TS service is singleton-per-worker;
  // we set options once per page load and leave them. Included for API
  // symmetry with the Python provider which does support disposal.
  return { dispose: () => {} };
}
